import { Link, useRoute } from "wouter";

import { CheckCircle } from "lucide-react";
import fivetran from "../assets/fivetran.png";
import full from "../assets/full.png";
import kafka from "../assets/kafka.png";

const serviceData = {
  // =========================================================
  // APACHE KAFKA
  // =========================================================

  "apache-kafka": {
    icon: (
      <img
        src={kafka}
        alt="Kafka"
        className="mx-auto h-20 w-20 object-contain"
      />
    ),

    title: "Apache Kafka",

    tagline:
      "High-throughput, fault-tolerant real-time streaming platform for modern data architectures.",

    overview:
      "Apache Kafka is a distributed event streaming platform capable of handling large volumes of real-time data. It enables real-time data pipelines, event-driven architectures, application integration, and streaming analytics with high availability and low latency.",

    benefits: [
      "High-throughput real-time data streaming",
      "Highly scalable and fault tolerant",
      "Event-driven microservices support",
      "Reliable data pipelines",
      "Real-time analytics and monitoring",
    ],

    useCases: [
      {
        title: "Fraud Detection",
        description: "Real-time fraud monitoring and transaction analysis.",
      },
      {
        title: "Real-time Analytics",
        description: "Live dashboards, metrics, and business insights.",
      },
      {
        title: "Event Sourcing",
        description: "Build event-driven microservices architectures.",
      },
      {
        title: "Monitoring",
        description: "Collect application logs, events, and system metrics.",
      },
    ],
  },

  // =========================================================
  // APACHE FLINK
  // =========================================================

  "apache-flink": {
    icon: "⚡",

    title: "Apache Flink",

    tagline:
      "Real-time stream processing with low latency and stateful computations.",

    overview:
      "Apache Flink is a distributed processing framework designed for stateful computations over bounded and unbounded data streams. It is widely used for real-time analytics, event processing, fraud detection, IoT processing, and streaming applications.",

    benefits: [
      "Low-latency stream processing",
      "Exactly-once processing guarantees",
      "Stateful stream processing",
      "Seamless Kafka integration",
      "Batch and stream processing",
    ],

    useCases: [
      {
        title: "IoT Processing",
        description: "Analyze and process IoT sensor data in real time.",
      },
      {
        title: "Recommendations",
        description: "Generate personalized recommendations using live events.",
      },
      {
        title: "Risk Analysis",
        description: "Perform real-time financial and operational risk analysis.",
      },
      {
        title: "Complex Event Processing",
        description: "Detect patterns across large streams of events.",
      },
    ],
  },

  // =========================================================
  // CONFLUENT CLOUD
  // =========================================================

  "confluent-cloud": {
    icon: "☁️",

    title: "Confluent Cloud",

    tagline:
      "Fully managed Kafka service with enterprise-grade security and scalability.",

    overview:
      "Confluent Cloud is a fully managed cloud-native event streaming platform built around Apache Kafka. It reduces the operational complexity of running Kafka infrastructure while providing scalability, security, monitoring, and integrations for modern data architectures.",

    benefits: [
      "No infrastructure management",
      "Automatic scaling",
      "Enterprise security and compliance",
      "Built-in monitoring",
      "Cloud-native integrations",
    ],

    useCases: [
      {
        title: "Cloud Migration",
        description: "Move Kafka workloads to a managed cloud platform.",
      },
      {
        title: "Global Data Sync",
        description: "Synchronize data across multiple regions and systems.",
      },
      {
        title: "Streaming Applications",
        description: "Build scalable real-time event-driven applications.",
      },
      {
        title: "Customer Data",
        description: "Create real-time customer data and analytics pipelines.",
      },
    ],
  },

  // =========================================================
  // FULL STACK DEVELOPMENT
  // =========================================================

  "full-stack-development": {
    icon: (
      <img
        src={full}
        alt="Full Stack"
        className="mx-auto h-16 w-16 object-contain"
      />
    ),

    title: "Full Stack Development",

    tagline:
      "End-to-end web and mobile application development with modern technologies.",

    overview:
      "We build scalable and maintainable applications using modern frontend frameworks and robust backend architectures. Our solutions cover UI development, APIs, databases, authentication, integrations, DevOps, and cloud deployment.",

    benefits: [
      "Modern UI/UX development",
      "API design and integration",
      "Scalable backend architectures",
      "Database development",
      "DevOps and cloud deployment",
    ],

    useCases: [
      {
        title: "E-commerce",
        description: "Scalable online stores and commerce platforms.",
      },
      {
        title: "Fintech",
        description: "Secure financial and payment applications.",
      },
      {
        title: "Healthcare",
        description: "Digital healthcare and patient management systems.",
      },
      {
        title: "Logistics",
        description: "Supply-chain and logistics management platforms.",
      },
    ],
  },

  // =========================================================
  // FIVETRAN
  // =========================================================

  fivetran: {
    icon: (
      <img
        src={fivetran}
        alt="Fivetran"
        className="mx-auto h-16 w-16 object-contain"
      />
    ),

    title: "Fivetran",

    tagline:
      "Automated data integration with reliable and low-maintenance pipelines.",

    overview:
      "Fivetran is a managed data integration platform that automatically moves data from various sources into destinations such as cloud data warehouses and data platforms. It simplifies data ingestion and helps organizations maintain reliable analytics pipelines.",

    benefits: [
      "Large collection of ready-to-use connectors",
      "Automated data pipelines",
      "Automatic schema changes",
      "Reliable data replication",
      "Analytics-ready data",
    ],

    useCases: [
      {
        title: "Marketing Analytics",
        description: "Combine marketing data from multiple platforms.",
      },
      {
        title: "Sales Reports",
        description: "Centralize sales and revenue data for analytics.",
      },
      {
        title: "Finance",
        description: "Automate financial data consolidation.",
      },
      {
        title: "Data Warehousing",
        description: "Load operational data into modern cloud warehouses.",
      },
    ],
  },

  // =========================================================
  // AI / MACHINE LEARNING
  // =========================================================

  "ai-ml": {
    icon: "🧠",

    title: "AI / Machine Learning",

    tagline:
      "Intelligent automation and predictive analytics powered by AI and machine learning.",

    overview:
      "We design and deploy AI and machine learning solutions for prediction, automation, optimization, natural language processing, computer vision, and intelligent decision-making. Our solutions help businesses convert data into actionable insights.",

    benefits: [
      "Custom machine learning models",
      "Natural language processing",
      "Computer vision solutions",
      "AI-powered automation",
      "Predictive analytics",
    ],

    useCases: [
      {
        title: "Churn Prediction",
        description: "Identify customers who are likely to leave.",
      },
      {
        title: "AI Chatbots",
        description: "Build intelligent AI-powered customer assistants.",
      },
      {
        title: "Quality Control",
        description: "Use computer vision for automated inspection.",
      },
      {
        title: "Recommendations",
        description: "Provide personalized recommendations to users.",
      },
    ],
  },

  // =========================================================
  // DATABRICKS
  // =========================================================

  databricks: {
    icon: (
      <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-xl bg-orange-500 text-4xl font-bold text-white">
        D
      </div>
    ),

    title: "Databricks",

    tagline:
      "Unified lakehouse platform for data engineering, analytics, AI, and machine learning.",

    overview:
      "Databricks is a cloud-based data and AI platform built around the lakehouse architecture. It brings data engineering, large-scale data processing, SQL analytics, data science, machine learning, and artificial intelligence together on a unified platform. Databricks works closely with technologies such as Apache Spark and Delta Lake to process and manage large volumes of data.",

    benefits: [
      "Modern lakehouse architecture",
      "Large-scale data processing with Apache Spark",
      "Data engineering and ETL pipelines",
      "Machine learning and AI development",
      "SQL analytics and business intelligence",
      "Real-time and streaming data processing",
    ],

    useCases: [
      {
        title: "Data Engineering",
        description:
          "Build scalable ETL and ELT pipelines for large volumes of data.",
      },
      {
        title: "Machine Learning",
        description:
          "Develop, train, track, and deploy machine learning models.",
      },
      {
        title: "Real-time Analytics",
        description:
          "Process streaming data for real-time dashboards and decisions.",
      },
      {
        title: "Generative AI",
        description:
          "Build AI applications using LLMs, RAG, vector search, and AI agents.",
      },
    ],
  },

  // =========================================================
  // SNOWFLAKE
  // =========================================================

  snowflake: {
    icon: (
      <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-xl bg-sky-500 text-4xl font-bold text-white">
        S
      </div>
    ),

    title: "Snowflake",

    tagline:
      "Cloud data platform for scalable data warehousing, analytics, data sharing, and AI workloads.",

    overview:
      "Snowflake is a cloud-native data platform designed for storing, processing, and analyzing large amounts of data. Its architecture separates storage from compute, allowing organizations to scale workloads independently. Snowflake provides SQL-based analytics, data engineering, data sharing, governance, and capabilities for modern AI and machine learning workloads.",

    benefits: [
      "Separation of storage and compute",
      "Highly scalable cloud data warehouse",
      "Powerful SQL analytics",
      "Independent compute scaling",
      "Secure data sharing",
      "Support for modern data engineering and AI workloads",
    ],

    useCases: [
      {
        title: "Data Warehousing",
        description:
          "Build scalable cloud data warehouses for enterprise analytics.",
      },
      {
        title: "Business Intelligence",
        description:
          "Provide clean and centralized data for dashboards and reporting.",
      },
      {
        title: "Data Sharing",
        description:
          "Securely share data between teams, applications, and organizations.",
      },
      {
        title: "Data Engineering",
        description:
          "Build data pipelines and transform large datasets using SQL and modern data tools.",
      },
    ],
  },

  // =========================================================
  // MICROSOFT FABRIC
  // =========================================================

  "microsoft-fabric": {
    icon: (
      <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-xl bg-purple-600 text-4xl font-bold text-white">
        F
      </div>
    ),

    title: "Microsoft Fabric",

    tagline:
      "Unified analytics platform combining data engineering, data science, warehousing, real-time analytics, and Power BI.",

    overview:
      "Microsoft Fabric is an end-to-end analytics platform from Microsoft that brings data integration, data engineering, data science, data warehousing, real-time intelligence, and business intelligence into one environment. Its OneLake storage layer provides a unified data foundation, while Power BI enables organizations to build interactive reports and dashboards.",

    benefits: [
      "Unified analytics platform",
      "Centralized OneLake data storage",
      "Data engineering with Spark",
      "Integrated data warehousing",
      "Data science and machine learning",
      "Real-time analytics",
      "Native Power BI integration",
    ],

    useCases: [
      {
        title: "Enterprise Analytics",
        description:
          "Create a centralized analytics environment for enterprise data.",
      },
      {
        title: "Power BI Reporting",
        description:
          "Build interactive dashboards and business intelligence reports.",
      },
      {
        title: "Data Engineering",
        description:
          "Build scalable data pipelines and process data using Spark.",
      },
      {
        title: "Real-time Intelligence",
        description:
          "Analyze streaming and real-time data for faster business decisions.",
      },
    ],
  },
};

// =========================================================
// SERVICE DETAIL COMPONENT
// =========================================================

export default function ServiceDetail() {
  const [match, params] = useRoute("/services/:slug");

  if (!match || !serviceData[params.slug]) {
    return (
      <div className="pt-32 text-center text-xl font-semibold">
        Service not found
      </div>
    );
  }

  const service = serviceData[params.slug];

  return (
    <div className="bg-gray-100 pb-20 pt-28">
      {/* =====================================================
          HERO
      ====================================================== */}

      <section className="bg-gray-900 py-16 text-center text-white">
        <div className="mx-auto max-w-4xl px-4">
          <div className="mb-6 text-6xl">{service.icon}</div>

          <h1 className="mb-4 text-4xl font-bold sm:text-5xl">
            {service.title}
          </h1>

          <p className="mx-auto max-w-3xl text-gray-300">
            {service.tagline}
          </p>
        </div>
      </section>

      {/* =====================================================
          CONTENT
      ====================================================== */}

      <section className="mx-auto max-w-7xl px-4 py-16">
        <div className="grid gap-10 lg:grid-cols-3">
          {/* =================================================
              LEFT CONTENT
          ================================================== */}

          <div className="space-y-8 lg:col-span-2">
            {/* Overview */}

            <div className="rounded-xl bg-white p-8 shadow">
              <h2 className="mb-4 text-2xl font-bold text-gray-800">
                Overview
              </h2>

              <p className="leading-relaxed text-gray-600">
                {service.overview}
              </p>
            </div>

            {/* Benefits */}

            <div className="rounded-xl bg-white p-8 shadow">
              <h2 className="mb-4 text-2xl font-bold text-gray-800">
                Benefits
              </h2>

              <ul className="space-y-4">
                {service.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle
                      className="mr-3 mt-1 flex-shrink-0 text-orange-500"
                      size={20}
                    />

                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Use Cases */}

            <div className="rounded-xl bg-white p-8 shadow">
              <h2 className="mb-6 text-2xl font-bold text-gray-800">
                Use Cases
              </h2>

              <div className="grid gap-6 sm:grid-cols-2">
                {service.useCases.map((useCase, index) => (
                  <div
                    key={index}
                    className="rounded-lg bg-gray-50 p-5 transition hover:bg-gray-100"
                  >
                    <h3 className="mb-2 font-semibold text-gray-800">
                      {useCase.title}
                    </h3>

                    <p className="text-sm leading-relaxed text-gray-600">
                      {useCase.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* =================================================
              CTA
          ================================================== */}

          <div className="h-fit rounded-xl bg-orange-500 p-8 text-white shadow-lg lg:sticky lg:top-28">
            <h3 className="mb-3 text-xl font-bold">
              Ready to get started?
            </h3>

            <p className="mb-6 text-sm leading-relaxed">
              Contact us for a tailored solution for your business.
            </p>

            <Link href="/contact">
              <a className="block rounded-md bg-white py-3 text-center font-semibold text-orange-500 transition hover:bg-gray-100">
                Contact Us
              </a>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}